// Phone validation utilities using libphonenumber-js.
// Replaces the previous regex-based validation with real per-country phone number parsing.

import {
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
  type PhoneNumber,
} from 'libphonenumber-js';

/**
 * BUG-2132 — read a value as ONE valid phone number, or nothing.
 *
 * `extract: false` makes the whole value the number: libphonenumber's default
 * pulls a number out of surrounding text, so "call 218-555-0142" read as the
 * bare number and the words were silently lost. An extension ("x12",
 * "ext. 12", ";ext=12") is part of one number and parses.
 *
 * @param value - The stored or typed phone.
 * @param defaultCountry - The country a number without a `+` is read in.
 * @returns The parsed number when the whole value is one valid number, else null.
 */
function parseWholePhone(value: string, defaultCountry: CountryCode): PhoneNumber | null {
  const parsed = parsePhoneNumberFromString(value.trim(), { defaultCountry, extract: false });
  return parsed != null && parsed.isValid() ? parsed : null;
}

export interface PhoneValidationResult {
  valid: boolean;
  error?: string;
  formatted?: string;
}

/**
 * Validate a phone number using libphonenumber-js.
 * @param phone - The phone number to validate (any format)
 * @param required - Whether the phone number is required (default: true)
 * @param defaultCountry - Fallback country for numbers without a country code (default: 'US')
 * @returns Validation result with formatted E.164 number if valid
 */
export function validatePhone(
  phone: string,
  required = true,
  defaultCountry: CountryCode = 'US',
): PhoneValidationResult {
  if (!phone.trim()) {
    if (required) {
      return { valid: false, error: 'Phone number is required' };
    }
    return { valid: true };
  }

  const parsed = parsePhoneNumberFromString(phone, defaultCountry);

  if (!parsed) {
    return { valid: false, error: 'Please enter a valid phone number' };
  }

  if (!parsed.isValid()) {
    return { valid: false, error: 'Please enter a valid phone number' };
  }

  return { valid: true, formatted: parsed.format('E.164') };
}

/**
 * Format a phone number for display on a screen.
 *
 * A number that shares the default country's calling code shows in national
 * format ("(218) 555-0142"); any other shows international ("+44 20 7946
 * 0958"), so a buyer can tell a foreign number is foreign (BUG-2132 AC 4, the
 * ADR 030 — Design Canon phone-display row as amended 2026-10-22). The calling
 * code, not the country, decides: a Canadian number is dialled the same way as
 * a US one from a US house, so it keeps the national look. An extension shows
 * as " ext. 12".
 *
 * A value that is not one valid number shows as stored, trimmed: a bare 10 or
 * 11 digit string still gets the US layout, and anything else (text, two
 * numbers, a value written with a leading "+") is never reduced to the
 * number inside it or reshaped into a US layout (BUG-2595).
 *
 * @param phone - The phone number to format
 * @param defaultCountry - Fallback country code (default: 'US')
 * @returns Formatted phone number string
 */
export function formatPhoneDisplay(
  phone: string,
  defaultCountry: CountryCode = 'US',
): string {
  if (!phone || !phone.trim()) return '';

  const parsed = parseWholePhone(phone, defaultCountry);
  if (parsed) {
    return parsed.countryCallingCode === getCountryCallingCode(defaultCountry)
      ? parsed.formatNational()
      : parsed.formatInternational();
  }

  // Fallback: basic US formatting for a raw 10-digit number — only when the
  // value IS digits and phone punctuation, so words around a number survive.
  const trimmed = phone.trim();
  if (!/^[\d\s().+-]+$/.test(trimmed)) return trimmed;
  // A "+" names its own country code, so an invalid one is not a US number
  // missing its punctuation: "+1573965670" stays as stored (BUG-2595).
  if (trimmed.startsWith('+')) return trimmed;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  return trimmed;
}

/**
 * Render a stored phone number (typically E.164 like "+15105555555") in
 * international format ("+1 510 555 5555") for **email and notification
 * templates only**. Falls back to the trimmed original when parsing fails so
 * we never lose information — partial info is more useful than nothing for a
 * support contact line where the buyer just needs *some* way to reach out.
 *
 * EMAIL-ONLY (BUG-1313, decision of record 2026-08-21). Anything rendered on a
 * SCREEN — admin or bidder, staff-facing or buyer-facing — uses
 * `formatPhoneDisplay` instead. An email crosses borders and arrives with no
 * tenant context, so the country code always earns its place; a web page the
 * reader reached through a tenant's own site needs it only for a foreign
 * number, which `formatPhoneDisplay` adds (BUG-2132). A screen consumer of
 * this function is a defect, and `phone-canon.guard.test.ts` fails CI on one.
 *
 * Deliberately distinct from `formatPhoneDisplay` — do NOT merge the two.
 * See ADR 030 Decision 3c, `Phone display` row.
 *
 * @param phone - The phone number to format (typically E.164 from the DB)
 * @returns International-formatted phone, the trimmed input on parse fail, or "" for blank input
 */
export function formatPhoneForFooter(phone: string): string {
  const trimmed = phone.trim();
  if (!trimmed) return '';
  const parsed = parsePhoneNumberFromString(trimmed);
  if (parsed && parsed.isValid()) return parsed.formatInternational();
  return trimmed;
}

/**
 * BUG-2132 — whether the whole value is one valid phone number (an extension
 * allowed). The contact phone rule judges with this, the same whole-value read
 * the storage, link and display helpers use, so a value the rule accepts is
 * always stored, linked and shown as a number. `validatePhone` pulls a number
 * out of surrounding text ("call 218-555-0142"), which those helpers then keep
 * as plain text.
 *
 * @param value - The phone as typed or sent.
 * @param defaultCountry - The country a number without a `+` is read in (default: 'US')
 * @returns True when the value is exactly one valid number.
 */
export function isOnePhoneNumber(value: string, defaultCountry: CountryCode = 'US'): boolean {
  return parseWholePhone(value, defaultCountry) != null;
}

/**
 * The `tel:` href for a stored or displayed phone, or null when it has none.
 *
 * Only ONE valid number gets a link, and the link is that number's RFC 3966
 * URI: "tel:+12185550142", or "tel:+12185550142;ext=12" so an extension dials
 * as an extension (BUG-2132 AC 1). Anything else returns null and the caller
 * renders the text without a link (AC 3): "call the office", two numbers in
 * one field, or a number too short to be one. The `sanitizePhoneHref` this
 * replaces joined every digit it found, so an extension dialled as part of the
 * number and two numbers became one 20-digit href.
 *
 * The href counterpart to `formatPhoneDisplay`: once a call site renders
 * "(705) 555-0103" as the link TEXT, the display string is the natural thing to
 * interpolate into an href, and `tel:(705) 555-0103` does not dial (BUG-1313
 * RAC 7). The `string | null` return makes every caller decide what a phone
 * with no link renders as.
 *
 * `phone-canon.guard.test.ts` flags a hand-built `tel:` followed by a dynamic
 * value; a `tel:` assembled some other way is not matched, so treat the guard
 * as a backstop, not a proof.
 *
 * @param value - The stored or displayed phone string
 * @param defaultCountry - The country a number without a `+` is read in (default: 'US')
 * @returns The full `tel:` URI, or null when the value is not one valid number
 */
export function phoneTelHref(value: string, defaultCountry: CountryCode = 'US'): string | null {
  if (!value.trim()) return null;
  return parseWholePhone(value, defaultCountry)?.getURI() ?? null;
}

/**
 * Normalize a phone number for storage-comparison in the verified-phone gate
 * (BUG-332 bidder change-detection + /api/profile; BUG-384 admin token binding).
 *
 * Both sides of every comparison run through this one function, so the defining
 * property is *equivalence*: two textual formats of the same number must
 * normalize to the same string. The verified-phone token is minted against the
 * WorkOS SMS factor phone (already canonical E.164) and consumed against the
 * caller-supplied value, so a real E.164 number must pass through untouched.
 *
 * Deliberately hand-rolled rather than delegating to `formatPhoneE164` /
 * libphonenumber-js: this function must be a pure, total string transform whose
 * equivalence classes never shift under a library upgrade, and whose `null`
 * contract (rather than `''`) the callers already branch on.
 *
 * Contract (BUG-404 — only assume US for unambiguously-US-shaped input):
 * - falsy / empty / separators-only             → null
 * - `+1` + exactly 10 NANP digits (area 2–9)    → kept as-is, separators stripped
 * - `+1` + anything else                        → null (malformed NANP)
 * - `+<other CC>` + 7–15 digits                 → kept as-is, separators stripped
 * - `+` followed by anything else               → null (malformed international)
 * - bare 10 NANP digits (area code 2–9)         → `+1` prepended
 * - bare 11 digits `1` + 10 NANP digits         → `+` prepended (no double `1`)
 * - anything else (non-US-shaped, letters)      → null  ← was silently corrupted
 *
 * That final `null` is the BUG-404 fix. Previously every non-`+` input had `+1`
 * blindly prepended, so a bare UK mobile `07911123456` became `+107911123456` —
 * an impossible number that never matched the WorkOS factor and silently
 * corrupted `public.users.phone` / `user_tenants.phone` for non-US users.
 * Returning null surfaces it as bad input at the caller (400 / no-op) instead.
 * International numbers still normalize correctly when entered with their `+`
 * country code. The 11-digit `1…` branch also fixes the former
 * `15551112222 → +115551112222` double-country-code case.
 *
 * The `+1` (NANP) branch is validated tightly — exactly 10 digits with an area
 * code starting 2–9 — rather than via the generic 7–15 digit check, because the
 * `PhoneInput` country selector assembles a `+`-prefixed value by prepending the
 * dial code to whatever digits the user typed. A UK mobile typed under the US
 * flag becomes `+107911123456` (12 digits, leading `0` national); that passed
 * the generic `+`-branch check and re-opened the same corruption path the bare
 * branch already closed (BUG-404 UI follow-up). A real US number is always
 * `+1` + 10 digits, and NANP area codes never start with 0 or 1, so the tighter
 * check rejects the malformed value without touching any valid one.
 *
 * @param phone - Raw or already-normalized phone string, or null/undefined.
 * @returns Normalized E.164 string, or null if the input is empty or not
 *          confidently resolvable to E.164 without an explicit country code.
 */
export function normalizePhoneForCompare(
  phone: string | null | undefined,
): string | null {
  if (!phone) return null;
  // Strip the common visual separators users (and libphonenumber-js output)
  // insert: whitespace, hyphens, parens, dots. Hyphen is last in the class so
  // it's an unambiguous literal, not a range.
  const stripped = phone.replace(/[\s().-]/g, '');
  if (!stripped) return null;

  // An explicit international prefix means the caller told us the country.
  // Trust it only when what follows the `+` is a plausible E.164 national
  // number. Otherwise reject rather than store junk like `+abc` (or a `+`
  // followed by leftover letters) as if it were a verified phone: the
  // comparison runs against the WorkOS factor's canonical E.164, and a
  // malformed value can only ever mismatch or poison a stored row.
  if (stripped.startsWith('+')) {
    // `+1` (NANP) is the one country code the PhoneInput selector can assemble
    // wrongly — prepending `+1` to a foreign national number (e.g. UK
    // `07911123456` → `+107911123456`). A real NANP number is exactly `+1` plus
    // 10 digits whose area code starts 2–9, so validate that shape rather than
    // the generic 7–15 digit check the malformed value would otherwise pass.
    if (stripped.startsWith('+1')) {
      return /^\+1[2-9]\d{9}$/.test(stripped) ? stripped : null;
    }
    // Other country codes: trust a plausible E.164 shape (7–15 digits).
    return /^\+\d{7,15}$/.test(stripped) ? stripped : null;
  }

  // Bare number: only safe to assume the US country code for digit strings
  // that are unambiguously US-shaped — 10 NANP digits (area code 2–9), or the
  // same with a leading `1` country code. A leading 0/1 area code is never a
  // valid NANP number, so reject it rather than corrupt it with a wrong `+1`.
  if (/^[2-9]\d{9}$/.test(stripped)) return `+1${stripped}`;
  if (/^1[2-9]\d{9}$/.test(stripped)) return `+${stripped}`;

  // Non-US-shaped or non-numeric bare input — reject rather than corrupt it
  // with a wrong `+1`. (BUG-404)
  return null;
}

/**
 * Format a phone number to E.164 format using libphonenumber-js.
 * @param phone - The phone number to format
 * @param defaultCountry - Fallback country code (default: 'US')
 * @returns E.164 formatted phone number or empty string if invalid
 */
export function formatPhoneE164(
  phone: string,
  defaultCountry: CountryCode = 'US',
): string {
  if (!phone || !phone.trim()) return '';

  const parsed = parsePhoneNumberFromString(phone, defaultCountry);
  if (parsed && parsed.isValid()) {
    return parsed.format('E.164');
  }

  // Fallback: strip to digits and assume US if 10 digits.
  // E.164 spec: max 15 digits (including country code).
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 10 || digits.length > 15) return '';

  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (phone.trim().startsWith('+')) return `+${digits}`;

  return `+${digits}`;
}

/**
 * The form a contact's phone is stored in (BL-2089 AC 13, BUG-2132).
 *
 * A valid number becomes E.164, so the bidder page's `tel:` link dials the same
 * way whether the row was typed, picked from the rep roster (which holds bare
 * digits) or copied from a customer. An extension rides after it as
 * " ext. 12" (BUG-2132 AC 1): readable, and every helper here parses it back
 * as the same number and extension. Anything that is not one number is kept
 * exactly as typed, trimmed: deliberately NOT `formatPhoneE164`, whose
 * digit-strip fallback turns `0044 20 7946 0958` into `+00442079460958`, and
 * never a number pulled out of surrounding text.
 *
 * @param raw - The phone as entered or copied; null when the row has none.
 * @returns The E.164 number (plus its extension), the trimmed input when it
 *   can't be read as one number, or null when there is no phone.
 */
export function toStoredContactPhone(raw: string | null): string | null {
  const trimmed = raw?.trim() ?? '';
  if (trimmed === '') return null;
  const parsed = parseWholePhone(trimmed, 'US');
  if (parsed == null) return trimmed;
  const e164 = parsed.format('E.164');
  return parsed.ext != null ? `${e164} ext. ${parsed.ext}` : e164;
}
